import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { format, formatDistanceToNow, differenceInSeconds } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  Building, MapPin, Clock, Euro, Users, TrendingUp, AlertTriangle, ArrowLeft,
  Gavel, ShoppingCart, Eye, Timer, Zap, CheckCircle, XCircle, Shield, Loader2,
  ChevronDown, ChevronUp, History, Bell, Lock, CreditCard
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

// Countdown Timer Component
const CountdownTimer = ({ endTime, onExpired, size = 'default' }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const end = new Date(endTime);
      const now = new Date();
      const diff = differenceInSeconds(end, now);
      
      if (diff <= 0) {
        onExpired?.();
        return { hours: 0, minutes: 0, seconds: 0, expired: true };
      }
      
      const hours = Math.floor(diff / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      const seconds = diff % 60;
      
      return { hours, minutes, seconds, expired: false, isUrgent: diff < 300 };
    };
    
    setTimeLeft(calculateTimeLeft());
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    
    return () => clearInterval(interval);
  }, [endTime, onExpired]);
  
  if (!timeLeft) return null;
  
  if (timeLeft.expired) {
    return <span className="text-red-600 font-bold">Gara Terminata</span>;
  }
  
  const sizeClasses = size === 'large' ? 'text-4xl' : 'text-xl';
  
  return (
    <div className={`flex items-center justify-center gap-2 font-mono ${timeLeft.isUrgent ? 'text-red-600' : 'text-gray-900'}`}>
      {timeLeft.isUrgent && <Zap className="h-6 w-6 text-red-600 animate-pulse" />}
      <div className={`flex items-center gap-1 ${sizeClasses} font-bold`}>
        <span className="bg-gray-100 px-3 py-2 rounded">{String(timeLeft.hours).padStart(2, '0')}</span>
        <span>:</span>
        <span className="bg-gray-100 px-3 py-2 rounded">{String(timeLeft.minutes).padStart(2, '0')}</span>
        <span>:</span>
        <span className={`px-3 py-2 rounded ${timeLeft.isUrgent ? 'bg-red-100 animate-pulse' : 'bg-gray-100'}`}>
          {String(timeLeft.seconds).padStart(2, '0')}
        </span>
      </div>
    </div>
  );
};

// Bid History Item
const BidHistoryItem = ({ bid, isHighest }) => (
  <div className={`flex items-center justify-between py-3 px-4 rounded-lg ${isHighest ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isHighest ? 'bg-green-500' : 'bg-gray-300'}`}>
        <span className="text-white font-bold">{bid.buyer_name?.[0] || '?'}</span>
      </div>
      <div>
        <p className={`font-medium ${isHighest ? 'text-green-700' : 'text-gray-700'}`}>
          {bid.buyer_name || 'Anonimo'}
        </p>
        <p className="text-sm text-gray-500">
          {format(new Date(bid.timestamp), 'HH:mm:ss', { locale: it })}
        </p>
      </div>
    </div>
    <div className="text-right">
      <p className={`font-bold ${isHighest ? 'text-green-700' : 'text-gray-700'}`}>
        €{bid.amount?.toLocaleString('it-IT')}
      </p>
      {isHighest && (
        <Badge className="bg-green-500 text-white text-xs">Vincente</Badge>
      )}
    </div>
  </div>
);

// Two-Step Bid Confirmation Dialog
const BidConfirmationDialog = ({ isOpen, onClose, amount, minIncrement, onConfirm, loading }) => {
  const [step, setStep] = useState(1);
  
  const handleConfirm = () => {
    if (step === 1) {
      setStep(2);
    } else {
      onConfirm();
    }
  };
  
  const handleClose = () => {
    setStep(1);
    onClose();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 1 ? (
              <>
                <Gavel className="h-5 w-5 text-[#0071e3]" />
                Conferma Offerta
              </>
            ) : (
              <>
                <Shield className="h-5 w-5 text-orange-500" />
                Conferma Finale
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {step === 1 
              ? 'Verifica i dettagli della tua offerta prima di procedere.'
              : 'Questa azione è irreversibile. Sei sicuro di voler procedere?'
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          {step === 1 ? (
            <div className="space-y-4">
              <div className="bg-[#0071e3]/5 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">La tua offerta:</p>
                <p className="text-3xl font-bold text-[#0071e3]">
                  €{amount?.toLocaleString('it-IT')}
                </p>
              </div>
              <div className="flex items-start gap-2 text-sm text-gray-600">
                <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                <p>
                  Effettuando questa offerta ti impegni legalmente all'acquisto 
                  dell'immobile qualora risultassi vincitore della gara.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-orange-700 font-medium mb-2">
                  <Lock className="h-4 w-4" />
                  Impegno Vincolante
                </div>
                <p className="text-sm text-orange-600">
                  Confermando questa offerta di <strong>€{amount?.toLocaleString('it-IT')}</strong> accetti 
                  le condizioni di vendita e ti impegni all'acquisto in caso di aggiudicazione.
                </p>
              </div>
              <div className="text-center py-2">
                <p className="text-sm text-gray-500">
                  Clicca "Conferma Offerta" per completare l'operazione
                </p>
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Annulla
          </Button>
          <Button 
            onClick={handleConfirm}
            disabled={loading}
            className={step === 2 ? 'bg-orange-500 hover:bg-orange-600' : 'bg-[#0071e3] hover:bg-[#0a3540]'}
            data-testid="confirm-bid-step"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Invio in corso...
              </>
            ) : step === 1 ? (
              'Procedi'
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Conferma Offerta
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Buy Now Confirmation Dialog
const BuyNowDialog = ({ isOpen, onClose, price, onConfirm, loading }) => {
  const [confirmed, setConfirmed] = useState(false);
  
  const handleClose = () => {
    setConfirmed(false);
    onClose();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-orange-500" />
            Acquista Subito
          </DialogTitle>
          <DialogDescription>
            Acquista immediatamente l'immobile al prezzo indicato.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          <div className="bg-orange-50 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-500 mb-1">Prezzo Acquista Subito:</p>
            <p className="text-3xl font-bold text-orange-600">
              €{price?.toLocaleString('it-IT')}
            </p>
          </div>
          
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="confirm-buy-now"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-1"
            />
            <label htmlFor="confirm-buy-now" className="text-sm text-gray-600">
              Confermo di voler acquistare immediatamente questo immobile al prezzo di 
              €{price?.toLocaleString('it-IT')} e accetto le condizioni di vendita.
            </label>
          </div>
        </div>
        
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Annulla
          </Button>
          <Button 
            onClick={onConfirm}
            disabled={loading || !confirmed}
            className="bg-orange-500 hover:bg-orange-600"
            data-testid="confirm-buy-now"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Elaborazione...
              </>
            ) : (
              <>
                <ShoppingCart className="h-4 w-4 mr-2" />
                Acquista Ora
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Detail Page Component
const GaraDetailPage = () => {
  const { competitionId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [competition, setCompetition] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showBidDialog, setShowBidDialog] = useState(false);
  const [showBuyNowDialog, setShowBuyNowDialog] = useState(false);
  const [showBidHistory, setShowBidHistory] = useState(true);
  const [hasDeposit, setHasDeposit] = useState(false);
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositAmount, setDepositAmount] = useState(0);
  
  const wsRef = useRef(null);
  
  // Fetch competition details
  const fetchCompetition = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/gara/${competitionId}`);
      setCompetition(response.data);
      setDepositAmount(response.data.deposito_cauzionale || 0);
    } catch (error) {
      console.error('Error fetching competition:', error);
      toast.error('Errore nel caricamento della gara');
    }
  }, [competitionId]);
  
  // Fetch bid history
  const fetchBids = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/gara/${competitionId}/bids`);
      setBids(response.data.bids || []);
    } catch (error) {
      console.error('Error fetching bids:', error);
    }
  }, [competitionId]);
  
  // Check deposit status
  const checkDeposit = useCallback(async () => {
    if (!user || !token || !competitionId) return;
    
    try {
      const response = await axios.get(`${API}/api/gara/${competitionId}/deposit-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasDeposit(response.data.has_deposit);
      if (response.data.deposito_cauzionale) {
        setDepositAmount(response.data.deposito_cauzionale);
      }
    } catch (error) {
      console.error('Error checking deposit:', error);
      setHasDeposit(false);
    }
  }, [user, token, competitionId]);
  
  // Pay deposit
  const handlePayDeposit = async () => {
    if (!user || !token) {
      toast.error('Devi effettuare il login per pagare il deposito');
      return;
    }
    
    setDepositLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/gara/deposit/checkout`,
        {
          competition_id: competitionId,
          origin_url: window.location.origin + `/gara/${competitionId}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.already_paid) {
        toast.success('Deposito già versato!');
        setHasDeposit(true);
      } else if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Errore durante il pagamento';
      toast.error(msg);
    } finally {
      setDepositLoading(false);
    }
  };
  
  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!competitionId) return;
    
    let ws = null;
    let reconnectTimer = null;
    let pingInterval = null;
    
    const connect = () => {
      const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
      const userParam = user ? `?user_id=${user.user_id}` : '';
      ws = new WebSocket(`${wsUrl}/ws/gara/${competitionId}${userParam}`);
      
      ws.onopen = () => {
        console.log('WebSocket connected for gara updates');
        // Setup ping to keep connection alive
        pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000);
      };
      
      ws.onmessage = (event) => {
        try {
          if (event.data === 'pong') return;
          
          const data = JSON.parse(event.data);
          
          if (data.type === 'init') {
            // Initial state from server
            console.log('Gara WS init:', data);
          } else if (data.type === 'new_bid') {
            // New bid received - update UI in real-time
            fetchBids();
            fetchCompetition();
            
            // Show toast for new bid
            toast.info(`Nuova offerta: €${data.bid_amount?.toLocaleString('it-IT')}`, {
              description: data.bidder_name || 'Anonimo'
            });
            
            if (data.extension_applied) {
              toast.warning('Tempo esteso! Anti-sniping attivato.', {
                icon: '⚡'
              });
            }
          } else if (data.type === 'competition_ended' || data.type === 'competition_closed') {
            fetchCompetition();
            toast.info('La gara è terminata!', { icon: '🏆' });
          } else if (data.type === 'status_update') {
            // Status refresh from server
            fetchCompetition();
          }
        } catch (e) {
          console.error('Error parsing WS message:', e);
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
      ws.onclose = () => {
        console.log('WebSocket disconnected');
        clearInterval(pingInterval);
        // Reconnect after 5 seconds
        reconnectTimer = setTimeout(connect, 5000);
      };
      
      wsRef.current = ws;
    };
    
    connect();
    
    return () => {
      clearTimeout(reconnectTimer);
      clearInterval(pingInterval);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [competitionId, fetchBids, fetchCompetition, user]);
  
  // Initial data fetch
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCompetition(), fetchBids()]);
      setLoading(false);
    };
    loadData();
  }, [fetchCompetition, fetchBids]);
  
  useEffect(() => {
    checkDeposit();
  }, [checkDeposit]);
  
  // Calculate minimum bid
  const minBid = competition
    ? (competition.current_highest_bid || competition.base_di_gara) + (competition.incremento_minimo || 1000)
    : 0;
  
  // Handle bid submission
  const handleSubmitBid = async () => {
    const amount = parseFloat(bidAmount.replace(/\D/g, ''));
    
    if (!amount || amount < minBid) {
      toast.error(`L'offerta minima è €${minBid.toLocaleString('it-IT')}`);
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await axios.post(
        `${API}/api/gara/bid`,
        {
          competition_id: competitionId,
          amount: amount
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        toast.success('Offerta registrata con successo!');
        setShowBidDialog(false);
        setBidAmount('');
        await Promise.all([fetchCompetition(), fetchBids()]);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante l\'invio dell\'offerta';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle buy now
  const handleBuyNow = async () => {
    setSubmitting(true);
    
    try {
      const response = await axios.post(
        `${API}/api/gara/buy-now`,
        { competition_id: competitionId },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        toast.success('Acquisto completato con successo!');
        setShowBuyNowDialog(false);
        await fetchCompetition();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante l\'acquisto';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Format bid input
  const handleBidInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setBidAmount(value ? parseInt(value).toLocaleString('it-IT') : '');
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-[#0071e3] animate-spin" />
      </div>
    );
  }
  
  if (!competition) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <XCircle className="h-16 w-16 text-red-400 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 mb-2">Gara non trovata</h2>
        <Button variant="outline" onClick={() => navigate('/gare')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Torna alle Gare
        </Button>
      </div>
    );
  }
  
  const isActive = competition.status === 'active';
  const canBid = user && user.user_type === 'buyer' && isActive;
  const canBuyNow = canBid && competition.buy_now_available && competition.buy_now_enabled;
  
  const statusColors = {
    scheduled: 'bg-blue-100 text-blue-800',
    active: 'bg-green-100 text-green-800',
    closing: 'bg-yellow-100 text-[#1D1D1F]',
    suspended: 'bg-red-100 text-red-800',
    awarded: 'bg-purple-100 text-purple-800',
    not_awarded: 'bg-gray-100 text-gray-800',
    under_review: 'bg-orange-100 text-orange-800',
    cancelled: 'bg-red-100 text-red-800'
  };
  
  const statusLabels = {
    scheduled: 'Programmata',
    active: 'In Corso',
    closing: 'In Chiusura',
    suspended: 'Sospesa',
    awarded: 'Aggiudicata',
    not_awarded: 'Non Aggiudicata',
    under_review: 'In Revisione',
    cancelled: 'Annullata'
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/gare')}
              className="text-gray-600 text-sm sm:text-base"
              size="sm"
            >
              <ArrowLeft className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Torna alle Gare</span>
              <span className="sm:hidden">Indietro</span>
            </Button>
            <Badge className={statusColors[competition.status] || 'bg-gray-100'}>
              {statusLabels[competition.status] || competition.status}
            </Badge>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Mobile: Bid Panel First */}
        <div className="lg:hidden mb-6">
          {/* Mobile Price Summary */}
          <div className="bg-white rounded-xl shadow-md p-4 mb-4">
            <div className="flex justify-between items-center mb-3">
              <span className="text-gray-500 text-sm">Offerta Attuale</span>
              <span className="font-bold text-xl text-green-600">
                €{(competition.current_highest_bid || competition.base_di_gara)?.toLocaleString('it-IT')}
              </span>
            </div>
            
            {isActive && (
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-xs text-gray-500 text-center mb-1">Tempo Rimanente</p>
                <CountdownTimer 
                  endTime={competition.end_datetime} 
                  size="default"
                  onExpired={() => fetchCompetition()}
                />
              </div>
            )}
            
            {canBid && (
              <div className="space-y-3">
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type="text"
                    value={bidAmount}
                    onChange={handleBidInputChange}
                    placeholder={`Min. €${minBid.toLocaleString('it-IT')}`}
                    className="pl-10 text-lg font-medium"
                    data-testid="mobile-bid-input"
                  />
                </div>
                <Button 
                  className="w-full bg-[#0071e3] hover:bg-[#0a3540] h-12"
                  onClick={() => setShowBidDialog(true)}
                  disabled={!bidAmount || parseInt(bidAmount.replace(/\D/g, '')) < minBid}
                >
                  <Gavel className="h-5 w-5 mr-2" />
                  Fai Offerta
                </Button>
              </div>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Property Image */}
            <div className="bg-white rounded-xl overflow-hidden shadow-md">
              <div className="relative h-48 sm:h-64 md:h-96">
                <img
                  src={competition.property?.photos?.[0] || '/placeholder-property.jpg'}
                  alt={competition.property?.address}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4 sm:p-6">
                <div className="flex items-center text-gray-500 text-sm mb-2">
                  <MapPin className="h-4 w-4 mr-1" />
                  {competition.property?.city}, {competition.property?.province}
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-4">
                  {competition.property?.address || 'Indirizzo non disponibile'}
                </h1>
                
                {/* Timer for active competitions - Desktop only (mobile has it in top panel) */}
                {isActive && (
                  <div className="hidden lg:block bg-gray-50 rounded-xl p-6 text-center">
                    <p className="text-sm text-gray-500 mb-2">Tempo Rimanente</p>
                    <CountdownTimer 
                      endTime={competition.end_datetime} 
                      size="large"
                      onExpired={() => fetchCompetition()}
                    />
                    {competition.extensions_count > 0 && (
                      <p className="text-sm text-orange-600 mt-2">
                        <Zap className="h-4 w-4 inline mr-1" />
                        Anti-sniping: {competition.extensions_count} estension{competition.extensions_count === 1 ? 'e' : 'i'}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Bid History */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <button
                onClick={() => setShowBidHistory(!showBidHistory)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <History className="h-5 w-5 text-gray-500" />
                  <span className="font-semibold">Storico Offerte ({bids.length})</span>
                </div>
                {showBidHistory ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </button>
              
              {showBidHistory && (
                <div className="px-4 pb-4 space-y-2 max-h-96 overflow-y-auto">
                  {bids.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Gavel className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      <p>Nessuna offerta ancora</p>
                      <p className="text-sm">Sii il primo a fare un'offerta!</p>
                    </div>
                  ) : (
                    bids.map((bid, index) => (
                      <BidHistoryItem 
                        key={bid.bid_id} 
                        bid={bid} 
                        isHighest={index === 0 && bid.status === 'valid'}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Sidebar - Bid Panel (Desktop Only) */}
          <div className="hidden lg:block space-y-6">
            {/* Price Card */}
            <div className="bg-white rounded-xl shadow-md p-6 sticky top-24">
              <h2 className="text-lg font-semibold mb-4">Dettagli Gara</h2>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-500">Valore Immobile</span>
                  <span className="font-medium">€{competition.valore_immobile?.toLocaleString('it-IT')}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-500">Base di Gara</span>
                  <span className="font-bold text-[#0071e3]">€{competition.base_di_gara?.toLocaleString('it-IT')}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-500">Incremento Minimo</span>
                  <span className="font-medium">€{competition.incremento_minimo?.toLocaleString('it-IT')}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-gray-500">Deposito Richiesto</span>
                  <span className="font-medium">€{competition.deposito_cauzionale?.toLocaleString('it-IT')}</span>
                </div>
                
                {competition.current_highest_bid && (
                  <div className="bg-green-50 rounded-lg p-4 -mx-2">
                    <div className="flex justify-between items-center">
                      <span className="text-green-700 flex items-center">
                        <TrendingUp className="h-4 w-4 mr-1" />
                        Offerta Attuale
                      </span>
                      <span className="font-bold text-xl text-green-700">
                        €{competition.current_highest_bid?.toLocaleString('it-IT')}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Bid Form */}
              {canBid && hasDeposit ? (
                <div className="mt-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      La tua offerta (min. €{minBid.toLocaleString('it-IT')})
                    </label>
                    <div className="relative">
                      <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        type="text"
                        value={bidAmount}
                        onChange={handleBidInputChange}
                        placeholder={minBid.toLocaleString('it-IT')}
                        className="pl-10 text-lg font-medium"
                        data-testid="bid-amount-input"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full bg-[#0071e3] hover:bg-[#0a3540] h-12 text-lg"
                    onClick={() => setShowBidDialog(true)}
                    disabled={!bidAmount || parseInt(bidAmount.replace(/\D/g, '')) < minBid}
                    data-testid="place-bid-button"
                  >
                    <Gavel className="h-5 w-5 mr-2" />
                    Fai Offerta
                  </Button>
                  
                  {canBuyNow && (
                    <>
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-200" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="bg-white px-2 text-gray-500">oppure</span>
                        </div>
                      </div>
                      
                      <Button
                        variant="outline"
                        className="w-full border-orange-500 text-orange-500 hover:bg-orange-50 h-12"
                        onClick={() => setShowBuyNowDialog(true)}
                        data-testid="buy-now-button"
                      >
                        <ShoppingCart className="h-5 w-5 mr-2" />
                        Acquista Subito - €{competition.buy_now_price?.toLocaleString('it-IT')}
                      </Button>
                    </>
                  )}
                </div>
              ) : canBid && !hasDeposit ? (
                <div className="mt-6">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <CreditCard className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-orange-800 mb-1">Deposito Cauzionale Richiesto</p>
                        <p className="text-sm text-orange-700">
                          Per partecipare a questa gara devi versare un deposito cauzionale di 
                          <strong> €{depositAmount?.toLocaleString('it-IT')}</strong>.
                        </p>
                        <p className="text-xs text-orange-600 mt-2">
                          Il deposito ti sarà restituito se non risulterai aggiudicatario.
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button 
                    className="w-full bg-orange-500 hover:bg-orange-600 h-12"
                    onClick={handlePayDeposit}
                    disabled={depositLoading}
                    data-testid="pay-deposit-button"
                  >
                    {depositLoading ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Reindirizzamento...
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-5 w-5 mr-2" />
                        Paga Deposito - €{depositAmount?.toLocaleString('it-IT')}
                      </>
                    )}
                  </Button>
                </div>
              ) : !user ? (
                <div className="mt-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800">
                      Per partecipare a questa gara devi essere registrato come acquirente.
                    </p>
                  </div>
                  <Link to="/registrazione/acquirente">
                    <Button className="w-full bg-[#0071e3] hover:bg-[#0a3540]">
                      Registrati come Acquirente
                    </Button>
                  </Link>
                </div>
              ) : user.user_type !== 'buyer' ? (
                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-[#1D1D1F]">
                    Solo gli acquirenti registrati possono partecipare alle gare.
                  </p>
                </div>
              ) : !isActive ? (
                <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600 text-center">
                    {competition.status === 'scheduled' 
                      ? 'La gara non è ancora iniziata.'
                      : 'Questa gara non è più attiva.'}
                  </p>
                </div>
              ) : null}
              
              {/* Agent Info */}
              {competition.agent && (
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm text-gray-500 mb-2">Agente Incaricato</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                      <span className="font-bold text-gray-600">
                        {competition.agent.first_name?.[0]}{competition.agent.last_name?.[0]}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">
                        {competition.agent.first_name} {competition.agent.last_name}
                      </p>
                      <p className="text-sm text-gray-500">{competition.agent.company_name}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Bid Confirmation Dialog */}
      <BidConfirmationDialog
        isOpen={showBidDialog}
        onClose={() => setShowBidDialog(false)}
        amount={parseInt(bidAmount.replace(/\D/g, '')) || 0}
        minIncrement={competition?.incremento_minimo || 1000}
        onConfirm={handleSubmitBid}
        loading={submitting}
      />
      
      {/* Buy Now Dialog */}
      <BuyNowDialog
        isOpen={showBuyNowDialog}
        onClose={() => setShowBuyNowDialog(false)}
        price={competition?.buy_now_price}
        onConfirm={handleBuyNow}
        loading={submitting}
      />
    </div>
  );
};

export default GaraDetailPage;
